import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  FileText, 
  Search, 
  Download,
  Calendar,
  Filter,
  RefreshCw,
  IndianRupee,
  Receipt,
  X,
  Phone,
  User,
  CreditCard,
  Briefcase,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { format, subDays } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';

const TellerReportsPage = ({ user, onLogout }) => {
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState({ total_amount: 0, total_count: 0, cancelled_count: 0 });
  const [services, setServices] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [counsellors, setCounsellors] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [counsellorFilter, setCounsellorFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Delete Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteStartDate, setDeleteStartDate] = useState('');
  const [deleteEndDate, setDeleteEndDate] = useState('');
  const [backupConfirmed, setBackupConfirmed] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch filter options
      const [servicesRes, methodsRes, counsellorsRes] = await Promise.all([
        api.get('/services'),
        api.get('/payment-methods'),
        api.get('/counsellors')
      ]);
      
      setServices(servicesRes.data);
      setPaymentMethods(methodsRes.data);
      setCounsellors(counsellorsRes.data);
      
      // Fetch report data
      await fetchReportData();
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReportData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (paymentMethodFilter && paymentMethodFilter !== 'all') params.append('payment_method_id', paymentMethodFilter);
      if (serviceFilter && serviceFilter !== 'all') params.append('service_id', serviceFilter);
      if (counsellorFilter && counsellorFilter !== 'all') params.append('counsellor_id', counsellorFilter);
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/teller/reports?${params.toString()}`);
      setPayments(response.data.payments || []);
      setSummary(response.data.summary || { total_amount: 0, total_count: 0, cancelled_count: 0 });
    } catch (error) {
      toast.error('Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, paymentMethodFilter, serviceFilter, counsellorFilter, searchQuery]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchReportData();
    }, 300);
    return () => clearTimeout(debounce);
  }, [startDate, endDate, paymentMethodFilter, serviceFilter, counsellorFilter, searchQuery]);

  const clearFilters = () => {
    setStartDate(format(subDays(new Date(), 7), 'yyyy-MM-dd'));
    setEndDate(format(new Date(), 'yyyy-MM-dd'));
    setPaymentMethodFilter('all');
    setServiceFilter('all');
    setCounsellorFilter('all');
    setSearchQuery('');
  };

  const exportToCSV = () => {
    if (payments.length === 0) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Receipt No', 'Date', 'Time', 'Patient Name', 'Phone', 'Service', 'Amount', 'Payment Method', 'Counsellor', 'Status', 'Remarks'];
    const rows = payments.map(p => [
      p.receipt_number,
      p.payment_date,
      format(new Date(p.created_at), 'HH:mm'),
      p.patient_name,
      p.patient_phone,
      p.service_name,
      p.amount,
      p.payment_method_name,
      p.counsellor_name,
      p.status,
      p.remarks || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `teller-report-${startDate}-to-${endDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  const openDeleteModal = () => {
    setDeleteStartDate('');
    setDeleteEndDate('');
    setBackupConfirmed(false);
    setDeleteModalOpen(true);
  };

  const handleBulkDelete = async () => {
    if (!deleteStartDate || !deleteEndDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    if (!backupConfirmed) {
      toast.error('Please confirm that backup has been exported');
      return;
    }

    try {
      setDeleteLoading(true);
      const response = await api.delete(`/payments/bulk-delete?start_date=${deleteStartDate}&end_date=${deleteEndDate}&backup_confirmed=true`);
      toast.success(response.data.message);
      setDeleteModalOpen(false);
      fetchReportData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entries');
    } finally {
      setDeleteLoading(false);
    }
  };

  // Access control - only Admin can view reports (Teller sees only their transactions)
  if (user?.role !== 'Admin' && user?.role !== 'Teller') {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="p-6 text-center">
          <p className="text-gray-500">Access denied</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <FileText className="w-7 h-7 text-green-600" />
              Teller Reports
            </h1>
            <p className="text-sm text-gray-500 mt-1">Payment transactions and analytics</p>
          </div>
          <div className="flex gap-2">
            {user?.role === 'Admin' && (
              <Button onClick={openDeleteModal} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Entries
              </Button>
            )}
            <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Collections</p>
                <p className="text-2xl font-bold text-green-600">₹{summary.total_amount.toLocaleString()}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-xl">
                <IndianRupee className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Receipts</p>
                <p className="text-2xl font-bold text-blue-600">{summary.total_count}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl">
                <Receipt className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cancelled</p>
                <p className="text-2xl font-bold text-red-600">{summary.cancelled_count}</p>
              </div>
              <div className="bg-red-100 p-3 rounded-xl">
                <X className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              Filters
            </h2>
            <Button variant="outline" size="sm" onClick={clearFilters}>
              Clear All
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
            {/* Date Range */}
            <div>
              <Label className="text-xs">Start Date</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div>
              <Label className="text-xs">End Date</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
              />
            </div>
            
            {/* Payment Method */}
            <div>
              <Label className="text-xs">Payment Method</Label>
              <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All Methods" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Methods</SelectItem>
                  {paymentMethods.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Service */}
            <div>
              <Label className="text-xs">Service</Label>
              <Select value={serviceFilter} onValueChange={setServiceFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All Services" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Counsellor */}
            <div>
              <Label className="text-xs">Counsellor</Label>
              <Select value={counsellorFilter} onValueChange={setCounsellorFilter}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="All Counsellors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Counsellors</SelectItem>
                  {counsellors.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Search */}
            <div>
              <Label className="text-xs">Search</Label>
              <div className="relative mt-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Phone or name..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Results Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">
              Transactions ({payments.length})
            </h3>
            {loading && <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Receipt</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date/Time</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Patient</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Counsellor</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-gray-500">
                      <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p>No transactions found</p>
                    </td>
                  </tr>
                ) : (
                  payments.map(payment => (
                    <tr key={payment.id} className={`hover:bg-gray-50 ${payment.status === 'cancelled' ? 'opacity-60' : ''}`}>
                      <td className="px-4 py-3">
                        <span className="font-mono text-sm text-gray-600">{payment.receipt_number}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-gray-900">{payment.payment_date}</div>
                        <div className="text-xs text-gray-500">{format(new Date(payment.created_at), 'hh:mm a')}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900">{payment.patient_name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {payment.patient_phone}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{payment.service_name}</td>
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${payment.status === 'cancelled' ? 'text-gray-400 line-through' : 'text-green-600'}`}>
                          ₹{payment.amount.toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <CreditCard className="w-3 h-3" />
                          {payment.payment_method_name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <User className="w-3 h-3" />
                          {payment.counsellor_name}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {payment.status === 'completed' ? (
                          <Badge className="bg-green-100 text-green-700">Completed</Badge>
                        ) : payment.status === 'cancelled' ? (
                          <Badge className="bg-red-100 text-red-700">Cancelled</Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-700">{payment.status}</Badge>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Entries Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Delete Payment Entries
            </DialogTitle>
            <DialogDescription>
              Permanently delete payment entries within a date range. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Warning Box */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Important Notice:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>This will permanently delete all payment entries in the selected date range</li>
                    <li>Appointment payment status (Paid badge) will NOT be affected</li>
                    <li>Export a backup CSV before proceeding</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={deleteStartDate}
                  onChange={(e) => setDeleteStartDate(e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>End Date *</Label>
                <Input
                  type="date"
                  value={deleteEndDate}
                  onChange={(e) => setDeleteEndDate(e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Backup Confirmation */}
            <div className="flex items-start space-x-3 bg-gray-50 p-4 rounded-lg">
              <Checkbox
                id="backup-confirmed"
                checked={backupConfirmed}
                onCheckedChange={setBackupConfirmed}
                className="mt-0.5"
              />
              <label htmlFor="backup-confirmed" className="text-sm text-gray-700 cursor-pointer">
                I confirm that I have exported a backup of the data before deletion
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkDelete} 
              disabled={!deleteStartDate || !deleteEndDate || !backupConfirmed || deleteLoading}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Entries
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TellerReportsPage;
